import React, { lazy } from 'react';
import '../assets/scss/plugins/extensions/dropzone.scss';
import {
  Router,
  Switch,
  Route as BaseRoute,
  Redirect as RouterRedirect,
} from 'react-router-dom';
import ScrollToTop from '../views/renderless/scroll-to-top/scroll-to-top.component';
import { history } from '../history';
import Route from './route.component';
import { ROUTE_SIGNUP, ROUTE_SIGNUP_STORE } from '../utility/constants';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { areDatesWithinMinutes } from '../utility/util/util.service';
import { unClosableNotification } from '../utility/notification/notification.service';
import { useIntl } from 'react-intl';

function retry(fn, retriesLeft = 25, interval = 2500) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // reject('maximum retries exceeded');
            reject(error);
            return;
          }

          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

const Redirect = lazy(() =>
  retry(() => import('../views/renderless/redirect/redirect.component')),
);

const TableSlugRedirect = lazy(() =>
  retry(() =>
    import('../views/renderless/table-redirect/table-redirect.component'),
  ),
);

const KioskSlugRedirect = lazy(() =>
  retry(() =>
    import('../views/renderless/kiosk-redirect/kiosk-redirect.component'),
  ),
);

const Menu = lazy(() => retry(() => import('../views/menu/menu.component')));

const Dashboard = lazy(() =>
  retry(() => import('../views/dashboard/dashboard.component')),
);

const StoreOrders = lazy(() => retry(() => import('../views/Order/OrderList')));

const TableReservations = lazy(() =>
  retry(() => import('../views/TableReservations/TableReservation')),
);

const StoreSettings = lazy(() => retry(() => import('../views/Settings')));

const SignUp = lazy(() =>
  retry(() => import('../views/sign-up/sign-up.component')),
);

const CreateStoreWizard = lazy(() =>
  retry(() =>
    import('../views/create-store-wizard/create-store-wizard.component'),
  ),
);

const NotFound = lazy(() =>
  retry(() => import('../views/errors/not-found.component')),
);

const InternalError = lazy(() =>
  retry(() => import('../views/errors/internal-error.component')),
);

const AdminArea = lazy(() =>
  retry(() => import('../views/admin-area/admin-area.component')),
);

const AppRouter = ({ children, me, store }) => {
  const intl = useIntl();
  useEffect(() => {
    if (me?.onBehalfOfStore && store) {
      const interval = setInterval(() => {
        const now = new Date();
        const endAt = new Date(me?.onBehalfOfStore?.sessionEndAt);
        if (
          areDatesWithinMinutes(now, endAt, 5) &&
          now.getTime() < endAt.getTime() &&
          history?.location?.pathname !== '/admin'
        ) {
          unClosableNotification(
            'warning',
            intl.formatMessage(
              { id: 'onBehalfOfTimeoutSoonNotification' },
              { minutes: 5, storeName: store.name },
            ),
            false,
            endAt?.getTime(),
          );
        }
        if (
          now.getTime() > endAt.getTime() &&
          history?.location?.pathname !== '/admin'
        ) {
          history.push('/admin');
        }
      }, 60000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [me, store]);

  return (
    <Router history={history}>
      <ScrollToTop />
      <Switch>
        <BaseRoute exact path="/">
          <RouterRedirect to="/dashboard" />
        </BaseRoute>

        <Route
          exact
          path={'/dashboard'}
          component={Dashboard}
          authRequired
          fetchStore
          redirectToSignUpStore
        />
        <Route
          exact
          path={'/admin'}
          component={AdminArea}
          authRequired
          fullLayout
          boxed
        />

        <Route
          exact
          path={'/dashboard/reservations'}
          component={TableReservations}
          authRequired
          fetchStore
          redirectToSignUpStore
        />

        <Route
          exact
          path={'/dashboard/orders'}
          component={StoreOrders}
          authRequired
          fetchStore
          redirectToSignUpStore
        />

        <Route
          path={'/dashboard/settings'}
          component={StoreSettings}
          authRequired
          fetchStore
          redirectToSignUpStore
        />

        <Route
          exact
          path={'/store/:slug'}
          component={Menu}
          fetchStore
          fetchUserOrders
        />
        <Route
          exact
          path={'/store/:slug/:categoryId'}
          component={Menu}
          fetchStore
          fetchUserOrders
        />

        <Route
          exact
          path={'/dynamic-table/:slug'}
          component={Menu}
          fetchStore
          fetchUserOrders
        />
        <Route
          exact
          path={'/self-service/:slug'}
          component={Menu}
          fetchStore
          fetchUserOrders
        />
        <Route exact path={'/table/:tableSlug'} component={TableSlugRedirect} />
        <Route exact path={'/kiosk/:kioskSlug'} component={KioskSlugRedirect} />
        <Route exact path={'/redirect'} component={Redirect} />

        <Route
          exact
          path={ROUTE_SIGNUP}
          component={SignUp}
          fullLayout
          flexBox
          boxed
        />
        <Route exact path={'/login'} authRequired />

        <Route
          exact
          path={ROUTE_SIGNUP_STORE}
          component={CreateStoreWizard}
          fullLayout
          authRequired
          fetchStore
          boxed
        />

        <Route exact path="/500" component={InternalError} fullLayout flexBox />
        <Route exact path="*" component={NotFound} fullLayout flexBox />
      </Switch>
      {children}
    </Router>
  );
};

const mapStateToProps = (state) => ({
  me: state.me.me,
  store: state.store.store,
});

export default connect(mapStateToProps, {})(AppRouter);
